import type { CollectionImageView, CollectionImagesPairParams, TagRef } from '@vizit/api';
import { FEATURE_FLAGS, NEEDS_CATEGORY_SCORE_LABEL } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { Asset, type AssetImageOverlayProps, AssetTileVariant, thumbnailSrcSet } from '@vizit/molecules';
import { ImageTags } from '@vizit/organisms';
import { readTileScore } from './collection-asset-grid.state';

/**
 * Individual collection asset component with hover actions
 */
interface CollectionAssetProps {
  image: CollectionImageView;
  /**
   * The pair the page picked: the tile shows the image's score for it (`pair_score`) instead of its own, dimmed while
   * that scoring runs. Unset shows the image's own score
   */
  pair?: CollectionImagesPairParams | null;
  /**
   * The image was just uploaded and is waiting for its score under the pair: its tile stays dimmed until it has one,
   * even before the pair is requested again
   * @default false
   */
  isAwaitingUploadScore?: boolean;
  /**
   * When the pair's scoring was last requested, by the API's clock: an image uploaded since waits for its score too
   */
  pairRequestedAt?: string | null;
  /**
   * A custom benchmark is picked: without a pair the tile shows no score rather than the image's own
   * @default false
   */
  isCustomBenchmark?: boolean;
  /**
   * No scoring can run without a category: the tile says so instead of waiting for a score
   * @default false
   */
  needsCategory?: boolean;
  onAssetClick?: (id: string) => void;
  onRemoveImageDialogOpen?: (id: string) => void;
  /**
   * Whether the asset is favorited for the workspace
   * @default false
   */
  isFavorite?: boolean;
  /**
   * Whether favoriting is unavailable to this user
   * @default false
   */
  isFavoriteDisabled?: boolean;
  /**
   * Called when the favorite toggle is pressed. When absent the action is not rendered.
   */
  onToggleFavorite?: () => void;
  /**
   * Called when "Download image" is pressed. When absent the action is not rendered.
   */
  onDownload?: () => void;
  tagging?: { workspaceId: string; tags: TagRef[] };
  heatmapOverlay?: AssetImageOverlayProps;
  columns?: number;
  /**
   * Multi-select for this tile (BLU-348). When absent the tile has no checkbox.
   */
  selection?: {
    isSelected: boolean;
    /** Something in the grid is selected, so every checkbox stays visible. */
    isSelectionMode: boolean;
    onSelectedChange: (options: { shiftKey: boolean }) => void;
  };
}

function CollectionAsset(props: CollectionAssetProps) {
  const { image, pair, isAwaitingUploadScore = false, pairRequestedAt, onAssetClick, onRemoveImageDialogOpen } = props;
  const { isFavorite = false, isFavoriteDisabled = false, onToggleFavorite, onDownload, selection } = props;
  const { tagging, heatmapOverlay, isCustomBenchmark, needsCategory, columns } = props;
  const showClassification = useFeatureFlag(FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION, false);

  const tileScore = readTileScore(image, pair, {
    isAwaitingUpload: isAwaitingUploadScore,
    pairRequestedAt,
    isCustomBenchmark,
    needsCategory,
  });
  const { score, isDimmed, isUnscored } = tileScore;

  const tile = (
    <Asset
      onPress={() => {
        onAssetClick?.(image.id);
      }}
      key={image.id}
      score={score}
      isUnscored={isUnscored}
      unscoredLabel={tileScore.needsCategory ? NEEDS_CATEGORY_SCORE_LABEL : 'Unscored'}
      isAiGenerated={image.source === 'generated'}
      isSelected={selection?.isSelected}
      data-asset-id={image.id}
    >
      {showClassification && <Asset.Label className="truncate">{image.classification ?? '\u00A0'}</Asset.Label>}
      <Asset.Image
        border
        name={image.name}
        src={image.url_360 ?? image.url}
        fallbackSrc={image.url}
        {...thumbnailSrcSet({ small: image.url_360, large: image.url_760 }, columns)}
        isDimmed={isDimmed}
        overlay={heatmapOverlay}
      />
      {selection && (
        <Asset.Checkbox
          aria-label={`Select ${image.name}`}
          isSelected={selection.isSelected}
          isAlwaysVisible={selection.isSelectionMode}
          onSelectedChange={(_, options) => selection.onSelectedChange(options)}
        />
      )}
      <Asset.Actions
        variant={AssetTileVariant.Sidebar}
        onFavorite={onToggleFavorite}
        isFavorite={isFavorite}
        isFavoriteDisabled={isFavoriteDisabled}
        onDownload={onDownload}
        onDelete={onRemoveImageDialogOpen ? () => onRemoveImageDialogOpen(image.id) : undefined}
      />
    </Asset>
  );

  if (!tagging) return tile;

  return (
    <div>
      {tile}
      <ImageTags image={image} tags={tagging.tags} workspaceId={tagging.workspaceId} />
    </div>
  );
}

export { CollectionAsset };
